import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-beratung";
const TITLE = "Shopify Beratung: Einschätzung statt Bauchgefühl";
const DESCRIPTION =
  "Shopify Beratung für Shopaufbau, Anpassungen und Conversion: was geprüft wird, welche Fragen zuerst geklärt gehören und was eine Beratung kostet.";

const faqs = [
  {
    q: "Was bringt eine Shopify Beratung?",
    a: "Sie wissen danach, welche Baustelle zuerst angefasst gehört, was sie kostet und ob sich der Aufwand für Ihren Shop überhaupt lohnt. Das erspart Umbauten, die nichts verändern.",
  },
  {
    q: "Was kostet eine Beratung?",
    a: "Eine erste Einschätzung zu Ihrem Vorhaben ist kostenlos und unverbindlich. Eine ausführliche Prüfung von Shop, Theme und Kaufweg rechne ich nach Aufwand mit 110 € netto pro Stunde ab.",
  },
  {
    q: "Was brauchen Sie von mir?",
    a: "Die Shop-Adresse und zwei Sätze zum Anliegen reichen für den Anfang. Für eine tiefere Prüfung sind Lesezugriff auf den Shop und die vorhandenen Zahlen aus Shopify Analytics hilfreich.",
  },
  {
    q: "Beraten Sie auch vor dem Shopstart?",
    a: "Ja. Vor dem Start geht es meist um Theme-Wahl, Sortimentsstruktur, Zahlungsarten, Versandlogik und darum, welche Apps wirklich nötig sind.",
  },
  {
    q: "Setzen Sie danach auch um?",
    a: "Wenn Sie möchten. Die Beratung ist nicht daran gebunden, Sie können die Ergebnisse auch selbst oder mit Ihrem Team umsetzen.",
  },
];

export const Route = createFileRoute("/shopify-beratung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Beratung · Shopify"
      title="Shopify Beratung: erst prüfen, dann umbauen"
      intro="Die meisten Shops haben nicht ein Problem, sondern eine Reihenfolge-Frage. Eine Beratung klärt, welche Änderung zuerst etwas bringt, was sie kostet und was Sie sich sparen können."
      source="shopify-beratung"
      sections={[
        {
          h: "Was geprüft wird",
          p: [
            "Ich sehe mir den Weg an, den Ihre Kunden tatsächlich gehen: Einstieg, Produktseite, Warenkorb, Kaufabschluss, dazu Ladezeit und mobile Bedienung.",
          ],
          list: [
            "Produktseite: Informationen, Varianten, Vertrauen, Verfügbarkeit",
            "Warenkorb und Kaufabschluss: Reibung, Versandkosten, Zahlungsarten",
            "Mobile Nutzung, weil dort meist die Mehrheit der Besucher unterwegs ist",
            "Technik: Ladezeit, Apps, Altlasten im Theme",
            "Zahlen: was Shopify Analytics über Abbrüche hergibt",
          ],
        },
        {
          h: "Was Sie danach in der Hand haben",
          p: [
            "Eine geordnete Liste: was zuerst, was danach, was kann warten. Dazu eine Aufwandsschätzung, damit Sie entscheiden können, was sich für Ihren Shop rechnet.",
            "Wenn sich eine Maßnahme bei Ihrem Traffic nicht lohnt, sage ich das. Das ist Teil der Beratung.",
          ],
        },
        {
          h: "Typische Anlässe",
          p: [],
          list: [
            "Der Shop soll neu aufgebaut werden und die Grundstruktur muss stimmen",
            "Besucher kommen, aber zu wenige bestellen",
            "Ein Vorgänger hat den Shop übergeben und niemand kennt den Stand",
            "Vor einer größeren Investition soll jemand unabhängig draufschauen",
          ],
        },
        {
          h: "Ablauf",
          p: [
            "Sie schildern Ihr Anliegen, ich melde mich innerhalb von 48 Stunden mit einer ersten Einschätzung und dem empfohlenen nächsten Schritt. Erst wenn Sie mehr wollen, wird daraus ein bezahlter Auftrag.",
          ],
        },
        {
          h: "Was eine Beratung nicht ist",
          p: [
            "Keine Garantie auf Umsatzsteigerung und keine Liste mit vierzig Punkten ohne Reihenfolge. Was sich verändert, hängt von Traffic, Sortiment, Marge und Umsetzung ab.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
        { to: "/shopify-freelancer", label: "Shopify Freelancer" },
        { to: "/roi-rechner", label: "Conversion-Rechner" },
      ]}
    />
  );
}
